/**
 * OnboardingBar — the one-question onboarding bar (core-flows §2).
 *
 * Renders BELOW the board (never above — value before configuration). Shows:
 *   "What's your morning?" → [Commuter] [Sports fan] [Investor] [Parent] [Skip →]
 *
 * Selecting a template:
 *   1. Fires template_selected analytics event.
 *   2. Swaps the board in-place with a crossfade (150ms fade + 200ms crossfade).
 *      Stable tiles (same type in both boards) are excluded from animation.
 *      Respects prefers-reduced-motion: no animation at all if set.
 *   3. Persists the new board to localStorage.
 *   4. Persists a dismissal flag (snip_onboarding_done) so the bar never re-appears.
 *
 * Skip → dismisses permanently without changing the board.
 *
 * Template selection does NOT fire board_customized (per metrics-plan §1.6 edge-case
 * rulings — template swap is not a self-directed customization op).
 */
import { useState, useEffect } from 'react';
import type { Board } from 'briefpane/schema';
import type { AnalyticsEmitter } from 'briefpane/contracts';
import { loadBoard } from 'briefpane/schema';
import { TEMPLATE_BOARDS } from '../templates/index.js';
import type { TemplateId } from '../templates/index.js';
import { buildEnvelope } from '../analytics/identity.js';
import { boardHash } from '../analytics/board-hash.js';

const ONBOARDING_DONE_KEY = 'snip_onboarding_done';

/** Mark onboarding as permanently dismissed. */
function persistDismissal(): void {
  try {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1');
  } catch { /* swallow */ }
}

/** Check if onboarding has been permanently dismissed. */
export function isOnboardingDone(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_DONE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Detect prefers-reduced-motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface TemplateOption {
  readonly id: TemplateId;
  readonly label: string;
}

const TEMPLATE_OPTIONS: readonly TemplateOption[] = [
  { id: 'commuter', label: 'Commuter' },
  { id: 'sports_fan', label: 'Sports fan' },
  { id: 'investor', label: 'Investor' },
  { id: 'parent', label: 'Parent' },
];

export interface OnboardingBarProps {
  readonly currentBoard: Board;
  readonly analytics: AnalyticsEmitter;
  readonly onBoardSwap: (newBoard: Board) => void;
  /**
   * Called with the new board after a template is selected, so the spaces layer
   * can persist it to SPACES_STORAGE_KEY. The bar itself no longer writes to
   * localStorage directly — all persistence flows through the spaces bridge.
   */
  readonly onBoardPersist: (newBoard: Board) => void;
}

export function OnboardingBar({
  currentBoard,
  analytics,
  onBoardSwap,
  onBoardPersist,
}: OnboardingBarProps): React.ReactElement | null {
  const [visible, setVisible] = useState(() => !isOnboardingDone());
  const [fading, setFading] = useState(false);

  // If dismissed in another tab, re-check on focus.
  useEffect(() => {
    function onFocus(): void {
      if (isOnboardingDone()) setVisible(false);
    }
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('focus', onFocus); };
  }, []);

  if (!visible) return null;

  function dismiss(): void {
    setFading(true);
    persistDismissal();
    // Wait for the fade-out animation before unmounting.
    setTimeout(() => { setVisible(false); }, 200);
  }

  function handleSelect(templateId: TemplateId): void {
    const rawBoard = TEMPLATE_BOARDS[templateId];
    const result = loadBoard(rawBoard);
    if (!result.ok) {
      // Template failed validation — skip silently (calm brand, no error toast).
      dismiss();
      return;
    }

    const newBoard = result.board;
    const replacedHash = boardHash(currentBoard);

    // Emit template_selected (NOT board_customized — §1.6 edge-case ruling).
    if (analytics.enabled) {
      try {
        analytics.emit({
          event: 'template_selected',
          template_id: templateId,
          entry_point: 'onboarding_question',
          replaced_board_hash: replacedHash,
          ...buildEnvelope(),
        });
      } catch { /* swallow */ }
    }

    // Persist through the spaces bridge (active space gets the new board) and swap.
    onBoardPersist(newBoard);
    onBoardSwap(newBoard);
    dismiss();
  }

  const reduced = prefersReducedMotion();
  const opacity = fading ? 0 : 1;
  const transition = reduced ? undefined : 'opacity 200ms ease';

  return (
    <div
      role="region"
      aria-label="Personalise your board"
      style={{
        borderTop: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-bg)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        opacity,
        transition,
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-delta)',
          color: 'var(--color-text-secondary)',
          whiteSpace: 'nowrap',
        }}
      >
        What's your morning?
      </span>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
        {TEMPLATE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => { handleSelect(opt.id); }}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg)',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--text-delta)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Skip personalisation"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-text-dim)',
          fontSize: 'var(--text-delta)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          padding: '4px 0',
        }}
      >
        Skip →
      </button>
    </div>
  );
}
